import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, Users, Calendar, DollarSign, Mail, Loader2, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import Sparkline from "@/components/Sparkline";
import OnboardingWizard from "@/components/OnboardingWizard";
import ReportBrandingCard from "@/components/ReportBrandingCard";
import CrossSellCard from "@/components/CrossSellCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const Tile = ({ icon: Icon, label, value, hint, tone = "slate" }) => {
  const map = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    indigo: "text-indigo-700",
    amber: "text-amber-700",
  };
  return (
    <Card className="p-5 border border-slate-200 rounded-2xl">
      <div className="flex items-start justify-between">
        <Icon size={18} className="text-slate-400" />
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">30d</span>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-3">{label}</div>
      <div className={`text-3xl font-extrabold mt-1 ${map[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </Card>
  );
};

export default function ClientGrowthDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onboardingKey, setOnboardingKey] = useState(0);

  const reloadDashboard = async () => {
    try {
      const [{ data: sum }, { data: tr }] = await Promise.all([
        axios.get(`${API}/my/summary`, { headers: httpAuth() }),
        axios.get(`${API}/my/trends?months=12`, { headers: httpAuth() }),
      ]);
      setData(sum); setTrends(tr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your dashboard.");
    }
  };

  useEffect(() => {
    document.title = "My Growth Dashboard | WebsiteTech.pro";
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    (async () => { await reloadDashboard(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/growth-reports/send-now`,
        { origin: window.location.origin },
        { headers: httpAuth() },
      );
      const msg = data.pdf_download_url
        ? `Report emailed — PDF ready to share.`
        : `Growth report ${data.status} to ${data.email}.`;
      toast.success(msg, {
        action: data.pdf_download_url
          ? { label: "Open PDF", onClick: () => window.open(data.pdf_download_url, "_blank") }
          : undefined,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the report."); }
    finally { setSending(false); }
  };

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-xl mx-auto py-24 text-center">
          <h1 className="text-4xl font-black text-slate-900">My Growth Dashboard</h1>
          <p className="text-slate-600 mt-3">Sign in to see your traffic → leads → appointments → revenue story.</p>
          <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800 rounded-full">
            <Link to="/login?redirect=/my" data-testid="my-signin-btn">Sign in</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const f = data?.funnel || {};
  const audit = data?.latest_audit;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <OnboardingWizard key={onboardingKey}
                         onDone={async () => {
                           setOnboardingKey((k) => k + 1);
                           await reloadDashboard();
                         }} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="my-dashboard">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold uppercase tracking-widest">Your Growth Story</div>
            <h1 className="text-4xl font-black text-slate-900 mt-3">Traffic → Leads → Appointments → Revenue</h1>
            <p className="text-slate-600 mt-2 text-sm max-w-2xl">Rolled up from every WebsiteTech surface you use. One place, one honest number, one clear next step.</p>
          </div>
          <Button onClick={sendReport} disabled={sending} className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="my-send-report">
            {sending ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</> : <><Mail size={14} className="mr-2" /> Email me this month&apos;s report</>}
          </Button>
        </div>

        {/* Funnel tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="my-funnel">
          <Tile icon={Users} label="Leads" value={f.leads_30d ?? 0} hint={`${f.leads_total ?? 0} total`} tone="indigo" />
          <Tile icon={Calendar} label="Appointments" value={f.appointments_30d ?? 0} hint={`${f.appointments_total ?? 0} total`} tone="amber" />
          <Tile icon={DollarSign} label="Revenue" value={`$${(f.revenue_30d ?? 0).toLocaleString()}`} hint="Paid invoices, last 30 days" tone="emerald" />
        </div>

        {/* 12-month trend row */}
        {trends?.months?.length > 0 && (
          <Card className="p-6 border border-slate-200 rounded-2xl mb-8" data-testid="my-trends">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Last 12 months</div>
                <div className="font-bold text-slate-900 text-lg">Momentum, not just the last 30 days</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div data-testid="trend-leads">
                <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold mb-2">Leads</div>
                <Sparkline
                  data={trends.months.map((m) => ({ value: m.leads, label: m.label }))}
                  strokeColor="#4338ca"
                  fillColor="rgba(99,102,241,0.12)"
                  ariaLabel="Leads trend"
                />
              </div>
              <div data-testid="trend-appointments">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-2">Appointments</div>
                <Sparkline
                  data={trends.months.map((m) => ({ value: m.appointments, label: m.label }))}
                  strokeColor="#b45309"
                  fillColor="rgba(245,158,11,0.14)"
                  ariaLabel="Appointments trend"
                />
              </div>
              <div data-testid="trend-revenue">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-2">Revenue</div>
                <Sparkline
                  data={trends.months.map((m) => ({ value: m.revenue, label: m.label }))}
                  strokeColor="#065f46"
                  fillColor="rgba(16,185,129,0.14)"
                  formatValue={(v) => `$${v}`}
                  ariaLabel="Revenue trend"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Score + actions row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="md:col-span-2 p-6 border border-slate-200 rounded-2xl" data-testid="my-score-card">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Latest Growth Score</div>
            {audit ? (
              <>
                <div className="mt-2 flex items-end gap-3">
                  <div className={`text-5xl font-black ${audit.score >= 70 ? "text-emerald-700" : audit.score >= 40 ? "text-amber-700" : "text-rose-700"}`} data-testid="my-score">{audit.score}</div>
                  <div className="text-slate-500 mb-1">/ 100</div>
                </div>
                <div className="text-sm text-slate-600 mt-1">Analyzed <span className="font-mono">{audit.url}</span></div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline" className="rounded-full">
                    <Link to="/growth-center" data-testid="my-open-growth">Open Growth Center <ArrowRight size={14} className="ml-1" /></Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="rounded-full">
                    <Link to="/lead-generation/audit" data-testid="my-run-new-audit">Run a fresh audit</Link>
                  </Button>
                </div>
              </>
            ) : (
              <div className="mt-3">
                <p className="text-slate-600 text-sm">Run your first audit to see your Growth Score and unlock the roll-up.</p>
                <Button asChild size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-full">
                  <Link to="/lead-generation/audit" data-testid="my-first-audit">Run Lead Generation Audit</Link>
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-6 border border-slate-200 rounded-2xl" data-testid="my-recos-card">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Pending recommendations</div>
            <div className="text-5xl font-black text-slate-900 mt-2">{data?.pending_recommendations ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">Approve or defer them in Growth Center.</div>
            <Button asChild size="sm" variant="outline" className="mt-4 rounded-full">
              <Link to="/growth-center">Review queue <ArrowRight size={14} className="ml-1" /></Link>
            </Button>
          </Card>
        </div>

        {/* Attribution */}
        <Card className="p-6 border border-slate-200 rounded-2xl mb-8" data-testid="my-attribution">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-slate-600" />
            <div className="font-bold text-slate-900">Where your leads came from (30 days)</div>
          </div>
          {(data?.attribution || []).length === 0 ? (
            <p className="text-sm text-slate-500">No leads recorded yet in this window.</p>
          ) : (
            <div className="space-y-2">
              {data.attribution.map((row) => (
                <div key={row.source} className="flex items-center gap-3" data-testid={`my-attr-${row.source}`}>
                  <div className="w-32 text-sm font-semibold text-slate-700 capitalize truncate">{row.source}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (row.leads / (data.attribution[0]?.leads || 1)) * 100)}%` }} />
                  </div>
                  <div className="w-10 text-right text-sm font-bold text-slate-900">{row.leads}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-slate-900">Your monthly report is on autopilot.</div>
              <div className="text-sm text-slate-600 mt-1">On the 1st of every month at 09:00 UTC we email you a client-branded recap: 30-day funnel, growth score, and the top 3 fixes to ship this month.</div>
            </div>
          </div>
        </Card>

        <div className="mt-8">
          <ReportBrandingCard />
        </div>

        <CrossSellCard />
      </main>
      <Footer />
    </div>
  );
}
